import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Check, CheckCircle, Building2, Dumbbell, Hotel, UtensilsCrossed, Bus,
  UserCheck, Headphones, Clipboard, Calendar, Trophy, GraduationCap,
  Users, Shield, AlertCircle, MessageSquare, FileText, Plane, Calculator,
  DollarSign, Sparkles, Award, Star, ChevronDown, MapPin, Apple,
  Target, Brain, Globe, Mountain, Clock
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useLanguage } from '../context/LanguageContext';
import { packagesTranslations } from '../translations/packages';
import { useManagedContent } from '../hooks/useManagedContent';
import '../App.css';

const featureIcons = [Building2, Hotel, UtensilsCrossed, Bus, UserCheck, Clipboard];
const highlightIcons = [null, null, Apple, null, Shield, null];
const accLocationIcons = [MapPin, MapPin];
const accCriteriaIcons = [MapPin, Star, Dumbbell, DollarSign];
const additionalServiceImages = [
  'https://i.postimg.cc/g0wLtVgj/Chat-GPT-Image-Jan-21-2026-03-45-07-PM.png',
  'https://i.postimg.cc/YS4LD6bP/Chat-GPT-Image-Jan-21-2026-03-45-50-PM.png',
  'https://i.postimg.cc/prjZNNC3/Chat-GPT-Image-Jan-21-2026-03-45-56-PM.png',
  'https://i.postimg.cc/DzgjwKq5/Chat-GPT-Image-Jan-21-2026-04-21-45-PM.png',
];
const additionalServiceDetailIcons = [
  [Trophy, Calendar, Target],
  [GraduationCap, Dumbbell, Brain, FileText, Apple, Users],
  [Star, Trophy, Globe, Clipboard, Users, Target],
  [MapPin, Mountain, Users, Dumbbell, UtensilsCrossed, Calendar],
];
const processIcons = [MessageSquare, FileText, CheckCircle, Calendar, Plane];
const trustBadgeIcons = [Award, Users, Star, Shield, Headphones, CheckCircle];

function Packages() {
  const [expandedAccommodation, setExpandedAccommodation] = useState(null);
  const [expandedFAQ, setExpandedFAQ] = useState(null);
  const { language } = useLanguage();
  const t = useManagedContent({
    pageKey: 'packages',
    language,
    defaultTranslations: packagesTranslations
  });

  const features = useMemo(() => t.features.map((f, i) => {
    const feat = { ...f, icon: featureIcons[i] };
    if (f.options) {
      feat.options = f.options.map((opt) => {
        const o = { ...opt };
        if (opt.locations) o.locations = opt.locations.map((text, k) => ({ icon: accLocationIcons[k], text }));
        if (opt.criteria) o.criteria = opt.criteria.map((text, k) => ({ icon: accCriteriaIcons[k], text }));
        return o;
      });
    }
    if (f.highlight && typeof f.highlight === 'object' && highlightIcons[i]) feat.highlight = { ...f.highlight, icon: highlightIcons[i] };
    return feat;
  }), [t]);

  const additionalServices = useMemo(() => t.additionalServices.map((s, i) => ({
    ...s,
    image: additionalServiceImages[i],
    details: s.details.map((text, j) => ({ icon: additionalServiceDetailIcons[i][j], text })),
    pricingNote: s.pricingNote ? { icon: DollarSign, text: s.pricingNote } : undefined,
    notice: s.notice ? { icon: AlertCircle, text: s.notice.text, subText: s.notice.subText } : undefined,
  })), [t]);

  const processSteps = useMemo(() => t.processSteps.map((s, i) => ({ ...s, number: i + 1, icon: processIcons[i] })), [t]);
  const pricingFactors = t.pricingFactors;
  const requiredInfo = t.requiredInfo;
  const optionalInfo = t.optionalInfo;
  const testimonials = useMemo(() => t.testimonials.map((x) => ({ ...x, rating: 5 })), [t]);
  const faqs = t.faqs;
  const trustBadges = useMemo(() => t.trustBadges.map((b, i) => ({ ...b, icon: trustBadgeIcons[i] })), [t]);

  return (
    <div className="App">
      <Header />
      
      {/* Hero Section */}
      <section className="packages-hero">
        <div className="packages-hero-background">
          <img 
            src="https://i.postimg.cc/DzW4prxz/Chat-GPT-Image-Jan-21-2026-03-45-01-PM.png" 
            alt="Sports team training together"
            loading="eager"
          />
        </div>
        <div className="packages-hero-overlay"></div>
        <div className="packages-hero-content">
          <motion.div 
            className="packages-hero-text"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <nav className="breadcrumb">
              <Link to="/">{t.hero.home}</Link>
              <span> / </span>
              <span>{t.hero.ourPackages}</span>
            </nav>
            <h1 className="packages-hero-heading">{t.hero.heading}</h1>
            <h2 className="packages-hero-subheading">{t.hero.subheading}</h2>
            <p className="packages-hero-tagline">{t.hero.tagline}</p>
          </motion.div>
        </div>
      </section>

      {/* Package Overview */}
      <section className="packages-overview-section">
        <div className="container">
          <motion.div 
            className="packages-overview-content"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <CheckCircle size={64} color="#C49818" />
            <h2 className="packages-overview-heading">{t.overview.heading}</h2>
            <h3 className="packages-overview-subheading">{t.overview.subheading}</h3>
            <p className="packages-overview-description">{t.overview.description}</p>
          </motion.div>
        </div>
      </section>

      {/* Package Includes - Main Features Grid */}
      <section className="packages-features-section">
        <div className="container">
          <motion.h2 
            className="section-heading"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            {t.featuresHeading}
          </motion.h2>
          
          <div className="packages-features-grid">
            {features.map((feature, index) => {
              const IconComponent = feature.icon;
              return (
                <motion.div
                  key={index}
                  className="package-feature-card"
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  whileHover={{ y: -4, boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)' }}
                >
                  <div className="package-feature-icon">
                    <IconComponent size={48} color="#C49818" />
                  </div>
                  <h3 className="package-feature-title">{feature.title}</h3>
                  <p className="package-feature-description">{feature.description}</p>
                  
                  {feature.details && (
                    <ul className="package-feature-list">
                      {feature.details.map((detail, idx) => (
                        <li key={idx}>
                          <Check size={20} color="#C49818" />
                          <span>{detail}</span>
                        </li>
                      ))}
                    </ul>
                  )}

                  {feature.hasAccordion && (
                    <div className="accommodation-options">
                      {feature.options.map((option) => (
                        <div 
                          key={option.id}
                          className={`accommodation-option ${expandedAccommodation === option.id ? 'expanded' : ''}`}
                          onClick={() => setExpandedAccommodation(expandedAccommodation === option.id ? null : option.id)}
                        >
                          <div className="accommodation-option-header">
                            <span className={`accommodation-badge ${option.badgeType}`}>
                              {option.badge}
                            </span>
                            <h4>{option.title}</h4>
                            <ChevronDown 
                              size={20} 
                              color="#C49818"
                              className={expandedAccommodation === option.id ? 'rotated' : ''}
                            />
                          </div>
                          {expandedAccommodation === option.id && (
                            <motion.div
                              key={option.id}
                              className="accommodation-option-content"
                              initial={{ opacity: 0, height: 0 }}
                              animate={{ opacity: 1, height: 'auto' }}
                              transition={{ duration: 0.3 }}
                            >
                              <p>{option.description}</p>
                              {option.locations && (
                                <div className="accommodation-locations">
                                  {option.locations.map((loc, locIdx) => {
                                    const LocIcon = loc.icon;
                                    return (
                                      <div key={locIdx} className="accommodation-location">
                                        <LocIcon size={16} color="#C49818" />
                                        <span>{loc.text}</span>
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                              {option.criteria && (
                                <div className="accommodation-criteria">
                                  {option.criteria.map((crit, critIdx) => {
                                    const CritIcon = crit.icon;
                                    return (
                                      <div key={critIdx} className="accommodation-criterion">
                                        <CritIcon size={16} color="#C49818" />
                                        <span>{crit.text}</span>
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                              {option.note && (
                                <div className="accommodation-note">
                                  {option.icon && <option.icon size={20} color="#C49818" />}
                                  <span>{option.note}</span>
                                </div>
                              )}
                              {option.benefit && (
                                <p className="accommodation-benefit">{option.benefit}</p>
                              )}
                            </motion.div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  {feature.highlight && (
                    <div className="package-feature-highlight">
                      {typeof feature.highlight === 'object' ? (
                        <>
                          {feature.highlight.icon && (
                            <feature.highlight.icon size={24} color="#C49818" />
                          )}
                          <span>{feature.highlight.text}</span>
                        </>
                      ) : (
                        <span>{feature.highlight}</span>
                      )}
                    </div>
                  )}
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Additional Services */}
      <section className="packages-additional-services-section">
        <div className="container">
          <motion.h2 
            className="section-heading"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            {t.additionalServicesHeading}
          </motion.h2>
          <motion.p 
            className="section-subheading"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            {t.additionalServicesSubheading}
          </motion.p>
          
          <div className="additional-services-list">
            {additionalServices.map((service, index) => (
              <motion.div
                key={index}
                className="additional-service-card"
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                whileHover={{ boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)' }}
              >
                <div className="additional-service-image">
                  <img src={service.image} alt={service.title} loading="lazy" />
                </div>
                <div className="additional-service-content">
                  <span className={`service-badge ${service.badgeType || 'gold'}`}>
                    {service.badge}
                  </span>
                  <h3 className="additional-service-title">{service.title}</h3>
                  <p className="additional-service-description">{service.description}</p>
                  
                  <div className="additional-service-details">
                    {service.details.map((detail, idx) => {
                      const DetailIcon = detail.icon;
                      return (
                        <div key={idx} className="service-detail-item">
                          <DetailIcon size={20} color="#C49818" />
                          <span>{detail.text}</span>
                        </div>
                      );
                    })}
                  </div>

                  {service.objectives && (
                    <ul className="service-objectives">
                      {service.objectives.map((obj, idx) => (
                        <li key={idx}>{obj}</li>
                      ))}
                    </ul>
                  )}

                  {service.pricingNote && (
                    <div className="service-pricing-note">
                      <service.pricingNote.icon size={20} color="#C49818" />
                      <span>{service.pricingNote.text}</span>
                    </div>
                  )}

                  {service.notice && (
                    <div className="service-notice">
                      <service.notice.icon size={20} color="#C49818" />
                      <div>
                        <p>{service.notice.text}</p>
                        <p className="service-notice-sub">{service.notice.subText}</p>
                      </div>
                    </div>
                  )}

                  <Link to="/book">
                    <motion.button
                      className="btn-service-request"
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      {service.buttonText}
                    </motion.button>
                  </Link>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works - Process */}
      <section className="packages-process-section">
        <div className="container">
          <motion.h2 
            className="section-heading"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            {t.processHeading}
          </motion.h2>
          <motion.p 
            className="section-subheading"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            {t.processSubheading}
          </motion.p>
          
          <div className="process-timeline">
            {processSteps.map((step, index) => {
              const StepIcon = step.icon;
              return (
                <motion.div
                  key={index}
                  className="process-step"
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                >
                  <div className="process-step-number">{step.number}</div>
                  {index < processSteps.length - 1 && <div className="process-step-connector"></div>}
                  <div className="process-step-icon">
                    <StepIcon size={32} color="#C49818" />
                  </div>
                  <h3 className="process-step-title">{step.title}</h3>
                  <p className="process-step-description">{step.description}</p>
                  {step.action && (
                    <span className="process-step-action">{step.action}</span>
                  )}
                  {step.timeline && (
                    <span className="process-step-timeline">
                      <Clock size={16} color="#C49818" />
                      {step.timeline}
                    </span>
                  )}
                  {step.support && (
                    <span className="process-step-support">
                      <Headphones size={16} color="#C49818" />
                      {step.support}
                    </span>
                  )}
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Pricing Information */}
      <section className="packages-pricing-section">
        <div className="container">
          <motion.div
            className="pricing-info-card"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <Calculator size={48} color="#C49818" />
            <h2 className="section-heading">{t.pricingHeading}</h2>
            <h3 className="section-subheading">{t.pricingSubheading}</h3>
            <p className="pricing-info-text">{t.pricingIntro}</p>
            
            <div className="pricing-factors-grid">
              {pricingFactors.map((factor, index) => (
                <div key={index} className="pricing-factor-item">
                  <Check size={20} color="#C49818" />
                  <span>{factor}</span>
                </div>
              ))}
            </div>

            <div className="pricing-highlight-box">
              <Sparkles size={24} color="#C49818" />
              <div>
                <p className="pricing-highlight-text">{t.pricingHighlight}</p>
                <p className="pricing-highlight-sub">{t.pricingHighlightSub}</p>
              </div>
            </div>

            <Link to="/book">
              <motion.button
                className="btn-primary large"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                {t.getCustomQuote}
              </motion.button>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* What to Send for a Quote */}
      <section className="packages-quote-section">
        <div className="container">
          <motion.div
            className="quote-request-card"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="section-heading">{t.quoteHeading}</h2>
            <h3 className="section-subheading">{t.quoteSubheading}</h3>
            
            <div className="quote-checklist">
              <div className="quote-checklist-section">
                <h4 className="quote-checklist-title">{t.requiredInfoTitle}</h4>
                <ul className="quote-checklist-list">
                  {requiredInfo.map((info, index) => (
                    <li key={index}>
                      <Check size={20} color="#C49818" />
                      <span>{info}</span>
                    </li>
                  ))}
                </ul>
              </div>
              
              <div className="quote-checklist-section">
                <h4 className="quote-checklist-title">{t.optionalInfoTitle}</h4>
                <ul className="quote-checklist-list optional">
                  {optionalInfo.map((info, index) => (
                    <li key={index}>
                      <Check size={20} color="#C49818" />
                      <span>{info}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="quote-cta-box">
              <h4>{t.readyToStart}</h4>
              <div className="quote-cta-buttons">
                <Link to="/book">
                  <motion.button
                    className="btn-primary large"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    {t.requestQuote}
                  </motion.button>
                </Link>
                <Link to="/contact">
                  <motion.button
                    className="btn-secondary large"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    {t.contactUs}
                  </motion.button>
                </Link>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="packages-testimonials-section">
        <div className="container">
          <motion.h2 
            className="section-heading"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            {t.testimonialsHeading}
          </motion.h2>
          
          <div className="testimonials-grid">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={index}
                className="testimonial-card"
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
              >
                <div className="testimonial-quote-icon">
                  <MessageSquare size={32} color="#C49818" />
                </div>
                <p className="testimonial-quote">"{testimonial.quote}"</p>
                <div className="testimonial-rating">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} size={16} color="#C49818" fill="#C49818" />
                  ))}
                </div>
                <p className="testimonial-name">{testimonial.name}</p>
                <p className="testimonial-team">{testimonial.team}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="packages-faq-section">
        <div className="container">
          <motion.h2 
            className="section-heading"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            {t.faqHeading}
          </motion.h2>
          <motion.p 
            className="section-subheading"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            {t.faqSubheading}
          </motion.p>
          
          <div className="faq-list">
            {faqs.map((faq, index) => (
              <motion.div
                key={index}
                className={`faq-item ${expandedFAQ === index ? 'expanded' : ''}`}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.3, delay: index * 0.05 }}
              >
                <div 
                  className="faq-question"
                  onClick={() => setExpandedFAQ(expandedFAQ === index ? null : index)}
                >
                  <span>{faq.question}</span>
                  <ChevronDown 
                    size={24} 
                    color="#C49818"
                    className={expandedFAQ === index ? 'rotated' : ''}
                  />
                </div>
                {expandedFAQ === index && (
                  <motion.div
                    className="faq-answer"
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    {faq.answer}
                  </motion.div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Trust Indicators */}
      <section className="packages-trust-section">
        <div className="container">
          <motion.h2 
            className="section-heading"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            {t.trustHeading}
          </motion.h2>
          
          <div className="trust-badges-grid">
            {trustBadges.map((badge, index) => {
              const BadgeIcon = badge.icon;
              return (
                <motion.div
                  key={index}
                  className="trust-badge"
                  initial={{ opacity: 0, scale: 0.8 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                >
                  <BadgeIcon size={48} color="#C49818" />
                  <h3>{badge.text}</h3>
                  <p>{badge.subText}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="packages-final-cta">
        <div className="packages-cta-background">
          <img 
            src="https://i.postimg.cc/QMsYDMWL/Chat-GPT-Image-Jan-21-2026-04-21-48-PM.png" 
            alt="Team celebrating"
            loading="lazy"
          />
          <div className="packages-cta-overlay"></div>
        </div>
        <div className="container">
          <motion.div
            className="packages-cta-content"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2>{t.finalCta.heading}</h2>
            <h3>{t.finalCta.subheading}</h3>
            <p>{t.finalCta.text}</p>
            <div className="packages-cta-buttons">
              <Link to="/book">
                <motion.button
                  className="btn-primary large"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  {t.finalCta.getQuote}
                </motion.button>
              </Link>
              <Link to="/locations">
                <motion.button
                  className="btn-secondary large"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  {t.finalCta.viewLocations}
                </motion.button>
              </Link>
            </div>
            <p className="packages-cta-phone">
              {t.finalCta.orCall} <a href="tel:+359885397102">+359 88 539 7102</a>
            </p>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

export default Packages;
